import { useEffect, useState } from 'react';
import {
  Anchor,
  Briefcase,
  Calendar,
  CreditCard,
  Mail,
  MessageSquare,
  Plug,
  Sparkles,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { supabase } from '../../lib/supabase';
import { cn } from '../../lib/cn';
import styles from './settings.module.css';

interface IntegrationCatalog {
  provider: string;
  label: string;
  description: string;
  icon: LucideIcon;
  phase: number;
}

const INTEGRATIONS: IntegrationCatalog[] = [
  { provider: 'icount', label: 'iCount', description: 'חשבוניות וחיובים — סנכרון אוטומטי מ-Quotes', icon: CreditCard, phase: 3 },
  { provider: 'resend', label: 'Resend', description: 'שליחת אימיילים יוצאים (התראות, חשבוניות, הצעות)', icon: Mail, phase: 1 },
  { provider: 'whatsapp_meta', label: 'WhatsApp Cloud', description: 'בוט WhatsApp (Meta Cloud API) — כבר חי דרך focusmode-website', icon: MessageSquare, phase: 1 },
  { provider: 'anthropic', label: 'Anthropic Claude', description: 'AI לסיכום שיחות וניקוד לידים', icon: Sparkles, phase: 2 },
  { provider: 'calendly', label: 'Calendly', description: 'הזמנת פגישות חיצונית — webhook לסנכרון אל /booking', icon: Calendar, phase: 5 },
  { provider: 'meta_ads', label: 'Meta Ads', description: 'מעקב קמפיינים ולידים מ-Facebook + Instagram', icon: Anchor, phase: 4 },
  { provider: 'google_ads', label: 'Google Ads', description: 'מעקב קמפיינים ולידים מ-Google', icon: Briefcase, phase: 4 },
  { provider: 'slack', label: 'Slack', description: 'התראות לערוץ Slack של החברה', icon: Plug, phase: 5 },
];

interface IntegrationRow {
  id: string;
  provider: string;
  status: 'disconnected' | 'connecting' | 'connected' | 'error';
  last_sync_at: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  disconnected: 'לא מחובר',
  connecting: 'מתחבר',
  connected: 'מחובר',
  error: 'שגיאה',
};

export function IntegrationsPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;

  const [rows, setRows] = useState<Record<string, IntegrationRow>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    const cancel = { current: false };
    void (async () => {
      const { data } = await supabase
        .from('integrations')
        .select('id, provider, status, last_sync_at')
        .eq('organization_id', orgId)
        .is('deleted_at', null);
      if (cancel.current) return;
      const byProvider: Record<string, IntegrationRow> = {};
      (data ?? []).forEach((r) => {
        byProvider[r.provider] = {
          id: r.id,
          provider: r.provider,
          status: r.status as IntegrationRow['status'],
          last_sync_at: r.last_sync_at,
        };
      });
      setRows(byProvider);
      setLoading(false);
    })();
    return () => { cancel.current = true; };
  }, [orgId]);

  return (
    <>
      <div className={styles.pageHead}>
        <h1>Integrations Hub</h1>
        <p className={styles.pageSub}>חיבורים חיצוניים. iCount הוא המוביל (Phase B Finance). השאר יחוברו לפי הצורך.</p>
      </div>

      <div className={styles.integrationGrid}>
        {INTEGRATIONS.map((item) => {
          const row = rows[item.provider];
          const status = row?.status ?? 'disconnected';
          const Icon = item.icon;
          return (
            <div key={item.provider} className={cn(styles.integrationCard, status === 'connected' && styles.integrationCardConnected)}>
              <div className={styles.integrationHead}>
                <div className={styles.integrationIcon} aria-hidden="true">
                  <Icon size={20} />
                </div>
                <div className={styles.integrationName}>
                  <strong>{item.label}</strong>
                  <span className={cn(styles.statusBadge, styles[`statusBadge--${status}`])}>
                    {STATUS_LABELS[status] ?? status}
                  </span>
                </div>
              </div>
              <p className={styles.integrationDesc}>{item.description}</p>
              <div className={styles.integrationFoot}>
                <span className={styles.muted}>Phase {String(item.phase)}</span>
                {status === 'connected' ? (
                  <button type="button" className={styles.btnSecondary} disabled>
                    מנותק (בקרוב)
                  </button>
                ) : (
                  <button type="button" className={styles.btnSecondary} disabled>
                    התחבר (בקרוב)
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {loading ? <p className={styles.notice}>טוען...</p> : null}

      <p className={styles.notice} style={{ marginBlockStart: 'var(--space-3)' }}>
        ⓘ כפתורי "התחבר" יופעלו כל אחד עם המודול הרלוונטי. iCount ב-M-P20 · Resend ב-M-P21 · Calendly ב-M-P24.
      </p>
    </>
  );
}
